import {
  useRegisterKitMutation,
  useRegisterMultiKitMutation,
} from '@viome/viome-base/src/usecase/Register/RegisterQuery';
import useRegisterStore, { RegistrationTrack } from '../useRegistrationStore';
import { formatDayJSDate } from '@/components/utils/utils';
import { useUserStore } from '../useUserStore';
import { useToast } from '../use-toast';
import { deSanitizedKitId } from './useRegistrationKitId';
import { ConsentRecord } from '@viome/viome-base/src/usecase/Register/Register.model';

const useConfirmRegister = () => {
  const { user: userData } = useUserStore();
  const {
    kitId,
    email,
    phone,
    password,
    firstName,
    lastName,
    country,
    apartment,
    street,
    city,
    state,
    zip,
    shippable,
    dob,
    gender,
    setFinalRegistrationCleanUp,
    phoneCode,
  } = useRegisterStore();
  const { toast } = useToast();

  const {
    isPending: isPendingRegistration,
    error: errorRegistration,
    mutate: mutateRegistration,
  } = useRegisterKitMutation();

  const {
    isPending: isPendingMultiKitRegistration,
    error: errorMultiKitRegistration,
    mutate: mutateMultiKitRegistration,
  } = useRegisterMultiKitMutation();

  const displayErrorMessage = (errorMessage: string) => {
    if (errorMessage === 'KIT_NOT_FOUND') {
      return 'Kit not found. Please Register with another Kit Id'
    } else {
      return 'Error while registering the Kit'
    }
  }

  const registrationPending =
    isPendingRegistration || isPendingMultiKitRegistration;
  const registrationError = errorRegistration || errorMultiKitRegistration;

  const onSubmit = (consentRecords: ConsentRecord[]) => {
    const postData = {
      user: {
        email,
        password,
        lastName,
        firstName,
        phone,
        countryPhoneCode: phoneCode.countryPhoneCode,
        smsConsent: false,
      },
      address: {
        country,
        addressLine1: street,
        addressLine2: apartment,
        zip,
        city,
        state,
        isShippable: shippable,
      },
      dateOfBirth: formatDayJSDate(dob),
      kitId: deSanitizedKitId(kitId),
      gender,
      consentRecords: consentRecords,
    };
    if (userData) {
      mutateMultiKitRegistration(postData, {
        onSuccess: (res) => {
          if (res?.registered) {
            setFinalRegistrationCleanUp(true, [
              RegistrationTrack.AccountCreation,
              RegistrationTrack.ContactInformation,
              RegistrationTrack.PersonalInformation,
              RegistrationTrack.AddressConfirmation,
              RegistrationTrack.RegistrationConfirmation,
              RegistrationTrack.TermsOfService,
              RegistrationTrack.ConsentForm,
            ]);
          }
        },
        onError: (error) => {
          toast({
            variant: 'destructive',
            description: displayErrorMessage(error.message),
          });
        },
      });
    } else {
      mutateRegistration(postData, {
        onSuccess: (res) => {
          if (res?.registered) {
            setFinalRegistrationCleanUp(true, [
              RegistrationTrack.AccountCreation,
              RegistrationTrack.ContactInformation,
              RegistrationTrack.PersonalInformation,
              RegistrationTrack.AddressConfirmation,
              RegistrationTrack.RegistrationConfirmation,
              RegistrationTrack.TermsOfService,
              RegistrationTrack.ConsentForm,
            ]);
          }
        },
        onError: (error) => {
          toast({
            variant: 'destructive',
            description: displayErrorMessage(error.message),
          });
        },
      });
    }
  };

  return {
    registrationPending,
    registrationError,
    onSubmit,
  };
};

export default useConfirmRegister;
